"use client";

import { useState, useEffect, Suspense } from "react";
import Image from "next/image";
import { Button, Card } from "@excelia-io/ui-lib";
import { config } from "config";
import { productsService, type Product } from "@/services/productsService";
import { userService } from "@/services/userService";
import toast from "react-hot-toast";
import type { ApiError } from "@/types";
import { useSearchParams } from "next/navigation";

interface PricingPlan {
  name: string;
  price: string;
  period: string;
  features: string[];
  buttonText: string;
  buttonLink: string;
  highlighted?: boolean;
}


const formatCurrency = (amount: string, currency: string): string => {
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return amount;
  
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase() || "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numAmount);
};


const formatPeriod = (duration: string): string => {
  const lowerDuration = duration.toLowerCase();
  if (lowerDuration.includes("month")) {
    return "/Month";
  } else if (lowerDuration.includes("year") || lowerDuration.includes("annual")) {
    return "/Year";
  }
  return `/${duration}`;
};

// Helper function to parse description into array of strings
const parseDescription = (description?: string | null): string[] => {
  if (!description) {
    return [];
  }

   try {
    const parsed = JSON.parse(description);
    if (Array.isArray(parsed)) {
      return parsed.filter((item) => typeof item === 'string');
    }
  } catch {
  
    if (description.includes('\n')) {
      return description.split('\n').filter((line) => line.trim().length > 0);
    }
    return [description];
  }

  return [];
};


const mapProductToPricingPlan = (product: Product): PricingPlan => {
  const price = formatCurrency(product.amount, product.currency);
  const period = formatPeriod(product.duration);
 
  const features = parseDescription(product.description);
  
  const buttonText = `Get Started for ${price}${period}`;
  const buttonLink = `/subscribe/${product.product_id}`;

  return {
    name: product.name,
    price,
    period,
    features,
    buttonText,
    buttonLink,
  };
};

export default function Home() {
  return (
    <Suspense>
      <HomeContent />
    </Suspense>
  );
}

function HomeContent() {
  const params = useSearchParams();
  const fromMyAccount = params.get("from") === "my-account";
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingProductId, setProcessingProductId] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const currentYear = new Date().getFullYear();

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const userData = await userService.getUserMe();
      setIsLoggedIn(!!userData.id);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const products = await productsService.getProducts();
        const mappedPlans = products.map(mapProductToPricingPlan);
        const sortedPlans = mappedPlans.sort((a, b) => {
          const aName = a.name.toLowerCase();
          const bName = b.name.toLowerCase();

          if (aName.includes("pay-by-month") || aName.includes("monthly")) return -1;
          if (bName.includes("pay-by-month") || bName.includes("monthly")) return 1;

          return a.name.localeCompare(b.name);
        });

        setPricingPlans(sortedPlans);
      } catch (err) {
        console.error("Failed to fetch products:", err);
        setError("Failed to load products. Please try again later.");
        // Fallback to empty array or you could set default plans here
        setPricingPlans([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
    fetchUserData();
  }, []);

  const handleLogin = () => {
    const currentHost = typeof window !== "undefined" ? window.location.origin : "";
    const redirectUrl = `${currentHost}/dashboard`;
    const loginUrl = `${config.apiBaseUrl}/auth/login?redirect_url=${encodeURIComponent(redirectUrl)}`;
    window.location.href = loginUrl;
  };

  const handleProductClick = async (productId: string, e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    
    if (processingProductId) {
      return; // Prevent multiple clicks
    }

    try {
      setProcessingProductId(productId);
      
      console.log("Product ID selected:", productId);
      
      const result = await productsService.initiatePurchase(productId);
      
      if (result.error) {
        setError(result.error);
        setProcessingProductId(null);
        return;
      }

      // Backend returns redirect_url in response, redirect to it
      if (result.redirect_url) {
        console.log("Redirecting to:", result.redirect_url);
        window.location.href = result.redirect_url;
      } else if (result.redirect && config.paymentsUrl) {
        // Fallback: use paymentsUrl from config
        console.log("Redirecting to payments URL:", config.paymentsUrl);
        window.location.href = config.paymentsUrl;
      } else {
        setError("Redirect URL not provided.");
        setProcessingProductId(null);
      }
    } catch (err) {
      console.error("Failed to initiate purchase:", err);
      setError("Failed to start purchase. Please try again.");
      setProcessingProductId(null);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Header */}
      <header className="relative bg-primary text-white overflow-hidden h-52">
        <div className="absolute top-0 right-0 w-64 h-64 pointer-events-none">
          <Image
            src="/logo-diamond.svg"
            alt="Diamond Pattern"
            width={256}
            height={256}
          />
        </div>

        {/* Header Navigation */}
        <div className="flex items-start justify-between px-8 pt-4 relative z-10">
          <div className="flex items-center gap-3">
            <Image
              src="/logo.svg"
              alt="Excelia Logo"
              width={320}
              height={320}
            />
          </div>
          {!(isLoggedIn && fromMyAccount) && (
            <Button
              onClick={handleLogin}
              className="mt-7 cursor-pointer rounded-none px-7 font-bold"
              style={{ backgroundColor: "#bdad95" ,color:'#1c5b6d' }}
            >
              Log In
            </Button>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-white pt-10 pb-4">
        <div className="w-full px-8 text-center">
          <h1 className="text-5xl font-normal text-gray-800">
            Let's get started with Excelia.
          </h1>
        </div>
      </section>

      {/* Pricing Plans Section */}
      <section className="flex-1 bg-white flex flex-col items-center justify-start pt-15 pb-12">
        <div className="w-full px-8">
          <div className="flex justify-center">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Loading products...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-600">{error}</p>
              </div>
            ) : pricingPlans.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">No products available at the moment.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl w-full items-stretch">
                {pricingPlans.map((plan, index) => {
                  const isEvenIndex = index % 2 === 0;
                  return (
                    <Card
                      key={index}
                      className={`flex flex-col h-full rounded-none py-10 px-6 ${isEvenIndex ? "bg-[#F2F0EC]" : "bg-[#E6ECED]"
                        }`}
                      title={
                        <div className="flex flex-col gap-2 items-center">
                          <h2 className={`text-2xl font-bold ${
                            isEvenIndex ? "text-gray-700" : "text-gray-900"
                          }`}>
                            {plan.name}
                          </h2>
                          {/* <div className="flex items-baseline gap-1">
                            <span className={`text-3xl font-bold ${
                              isEvenIndex ? "text-gray-800" : "text-gray-900"
                            }`}>
                              {plan.price}
                            </span>
                            <span className={`text-lg ${
                              isEvenIndex ? "text-gray-600" : "text-gray-700"
                            }`}>
                              {plan.period}
                            </span>
                          </div> */}
                        </div>
                      }
                    >
                      <div className="flex flex-col justify-between h-full">
                        <ul className={`space-y-4 ${isEvenIndex?'mb-16':'mb-6'}`}>
                          {plan.features.length > 0 ? (
                            plan.features.map((feature, featureIndex) => (
                              <li
                                key={featureIndex}
                                className={`flex items-start gap-3 text-base  ${
                                  isEvenIndex
                                    ? "text-gray-600"
                                    : "text-gray-700"
                                }`}
                              >
                                <Image
                                  src="/check-mark.svg"
                                  alt="Check mark"
                                  width={20}
                                  height={20}
                                  className="flex-shrink-0 mt-0.5"
                                />
                                <span>{feature}</span>
                              </li>
                            ))
                          ) : (
                            <li className={`text-sm ${
                              isEvenIndex ? "text-gray-500" : "text-gray-600"
                            }`}>
                              No features listed
                            </li>
                          )}
                        </ul>

                        <Button
                          className="w-full text-lg rounded-none"
                          style={{
                            backgroundColor: isEvenIndex ? "#406C6E" : "#1C5B6D",
                          }}
                          onClick={(e) => {
                            // Extract product_id from buttonLink (format: /subscribe/{product_id})
                            const productId = plan.buttonLink.replace("/subscribe/", "");
                            handleProductClick(productId, e);
                          }}
                          disabled={processingProductId !== null}
                        >
                          {processingProductId === plan.buttonLink.replace("/subscribe/", "")
                            ? "Processing..."
                            : plan.buttonText}
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-8">
        <div className="max-w-7xl mx-auto px-8 text-center text-sm text-gray-700">
          <p className="mb-1">
            Need support? Email us at{" "}
            <a
              href="mailto:support@myexcelia.com"
              className="font-semibold text-gray-900"
            >
              support@myexcelia.com
            </a>
          </p>
          <p>© Copyright {currentYear} Excelia. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
